import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import api from "../../lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Badge } from "@/components/ui/Badge";
import { Textarea } from "@/components/ui/Textarea";
import { InfoMessage } from "@/components/ui/InfoMessage";
import { ChevronLeft, Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Eye, EyeOff, ArrowLeft, UserCog, X } from "lucide-react";
import toast from "react-hot-toast";

const EditUserPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    roles: [],
    is_active: true,
    notes: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState("");

  // Fetch available roles
  const { data: rolesData } = useQuery(["roles"], async () => {
    const response = await api.get("/roles");
    return response.data;
  });

  // Fetch user data
  const {
    data: user,
    isLoading,
    error,
  } = useQuery(
    ["user", id],
    async () => {
      const response = await api.get(`/users/${id}`);
      return response.data.data;
    },
    {
      onSuccess: (userData) => {
        setFormData({
          first_name: userData.first_name || "",
          last_name: userData.last_name || "",
          email: userData.email || "",
          password: "",
          confirmPassword: "",
          roles: userData.roles || [],
          is_active: userData.is_active ?? true,
          notes: userData.notes || "",
          report_names: userData.report_names || "",
        });
      },
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );

  const updateMutation = useMutation((userData) => api.put(`/users/${id}`, userData), {
    onSuccess: (response) => {
      const updated = response?.data?.data;
      if (updated) {
        // Sync edit page cache immediately
        queryClient.setQueryData(["user", id], updated);
      }

      // Refresh users list and role counts
      queryClient.invalidateQueries(["users"]);
      queryClient.invalidateQueries(["user", id]);
      queryClient.invalidateQueries(["roles-with-counts"]);

      toast.success("User updated successfully");
      navigate("/users");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update user");
    },
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleRoleAdd = (roleId) => {
    const role = availableRoles.find((r) => r.id === parseInt(roleId));
    if (role && !formData.roles.find((r) => r.id === role.id)) {
      setFormData((prev) => ({
        ...prev,
        roles: [...prev.roles, role],
      }));
    }
    setSelectedRoleId(""); // Always reset the select value
  };

  const handleRoleRemove = (roleId) => {
    setFormData((prev) => ({
      ...prev,
      roles: prev.roles.filter((r) => r.id !== roleId),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if all required fields are empty (excluding password fields since they're optional in edit)
    const allFieldsEmpty =
      !formData.first_name.trim() &&
      !formData.last_name.trim() &&
      !formData.email.trim() &&
      (!formData.roles || formData.roles.length === 0);

    if (allFieldsEmpty) {
      toast.error("Please fill in the data before updating");
      return;
    }

    // Check individual fields and show specific messages
    let hasErrors = false;

    if (!formData.first_name.trim()) {
      toast.error("First name is required");
      hasErrors = true;
    }

    if (!formData.last_name.trim()) {
      toast.error("Last name is required");
      hasErrors = true;
    }

    if (!formData.email.trim()) {
      toast.error("Email address is required");
      hasErrors = true;
    }

    if (!formData.roles || formData.roles.length === 0) {
      toast.error("Please select at least one role");
      hasErrors = true;
    }

    // If password is provided, validate it
    if (formData.password) {
      if (formData.password !== formData.confirmPassword) {
        toast.error("Passwords do not match");
        hasErrors = true;
      }
      if (formData.password.length < 6) {
        toast.error("Password must be at least 6 characters long");
        hasErrors = true;
      }
    }

    // If there are validation errors, don't submit
    if (hasErrors) {
      return;
    }

    const { confirmPassword, ...userData } = formData;
    userData.role_ids = formData.roles.map((r) => r.id);
    delete userData.roles;

    // Remove password from update if it's empty
    if (!userData.password) {
      delete userData.password;
    }

    updateMutation.mutate(userData);
  };

  const availableRoles = rolesData?.data || [];
  const unselectedRoles = availableRoles.filter(
    (role) => !formData.roles.find((r) => r.id === role.id)
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-lg">Loading user...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <p className="mb-4 text-red-600">Failed to load user</p>
          <Button onClick={() => navigate("/users")}>Back to Users</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="w-full">
        {/* Form */}
        <div className="slide-up">
          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            <div>
              {formData.report_names && formData.report_names.length > 0 && (
                <InfoMessage>
                  Cannot delete this user while they are assigned to reports.
                </InfoMessage>
              )}
            </div>
            {/* Name Fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label
                  htmlFor="first_name"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  First Name *
                </Label>
                <Input
                  id="first_name"
                  name="first_name"
                  type="text"
                  placeholder="John"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="last_name"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Last Name *
                </Label>
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  placeholder="Doe"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled
                />
              </div>
            </div>

            {/* Roles Field */}
            <div className="space-y-2">
              <Label className="pl-3 text-xs font-medium uppercase text-gray-0">Roles *</Label>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  {unselectedRoles.length > 0 ? (
                    <Select
                      onValueChange={handleRoleAdd}
                      value={selectedRoleId}
                      key={formData.roles.length}
                    >
                      <SelectTrigger className="border-primary/20 focus:border-primary/40 rounded-t-lg">
                        <SelectValue placeholder="Add a role" />
                      </SelectTrigger>
                      <SelectContent className="z-50 bg-navy-100 text-gray-400">
                        {unselectedRoles.map((role) => (
                          <SelectItem key={role.id} value={role.id.toString()}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="border-primary/20 flex min-h-[44px] items-center rounded-t-lg border bg-gray-50 p-3 text-sm text-gray-400">
                      All roles selected
                    </div>
                  )}
                </div>
                <div>
                  {formData.roles.length > 0 ? (
                    <div className="flex min-h-[44px] flex-wrap gap-2 rounded-t-lg bg-navy-100 p-3">
                      {formData.roles.map((role) => (
                        <Badge
                          key={role.id}
                          variant="default"
                          className="flex items-center gap-1 px-3 py-1 text-gray-0"
                        >
                          {role.name}
                          <button
                            type="button"
                            onClick={() => handleRoleRemove(role.id)}
                            className="ml-1 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="border-primary/20 flex min-h-[44px] items-center rounded-t-lg border bg-gray-50 p-3 text-sm text-gray-400">
                      No roles selected
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label
                  htmlFor="password"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Leave blank to keep current"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="hover:text-primary absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="confirmPassword"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Confirm New Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="hover:text-primary absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="pl-3 text-xs font-medium uppercase text-gray-0">
                Notes
              </Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Add notes here..."
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="border-primary/20 text-primary focus:ring-primary h-4 w-4 rounded focus:ring-2"
              />
              <Label htmlFor="is_active" className="pl-3 text-xs font-medium uppercase text-gray-0">
                Active User
              </Label>
            </div>



            {/* Submit Buttons */}
            <div className="flex justify-end border-b border-t border-gray-0">
              <div className="flex w-auto items-center justify-center">
                <Link className="rounded-b-none border-r border-gray-0" to="/users">
                  <Button
                    variant="secondary"
                    className="flex h-auto items-center justify-center rounded-none px-10 py-5 text-xs font-normal uppercase text-gray-0 hover:scale-100"
                  >
                    <ChevronLeft className="mr-2 h-4 w-4 text-red-500" />
                    Back
                  </Button>
                </Link>
                <Button
                  className="flex h-auto items-center justify-center rounded-none rounded-b-none border-0 bg-transparent px-10 py-5 text-xs font-normal uppercase text-red-500 hover:scale-100"
                  type="submit"
                  disabled={updateMutation.isLoading}
                >
                  {updateMutation.isLoading ? "Updating..." : "Update"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUserPage;
